import { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  useFloating,
  offset,
  flip,
  shift,
  size as floatingSize,
  autoUpdate,
  FloatingPortal,
  useClick,
  useDismiss,
  useInteractions,
} from "@floating-ui/react";
import { TagsContext } from "../firebaseio/TagsContext";
import { UserContext } from "../firebaseio/UserContext";
import {
  firestoreCreateTag,
  firestoreUpdateTransaction,
} from "../firebaseio/firestoreIo";
import { RyeDropDownMultiSelect } from "../rye/RyeDropDownMultiSelect";
import { RyeIcon } from "../rye/RyeIcon";
import { TransactionRowContext } from "./TransactionRow";
import { TransactionColumnSizer } from "./TransactionColumnSizer";
import { TransactionColumn } from "./TransactionColumn";
import { buildClasses } from "../utils/buildClasses";
import { getTagColor, pickNextTagColorId } from "../tags_screen/tagColors";
import { TagDot } from "../tags_screen/TagDot";

export function TransactionColumnValueTags(props: {
  transactionId: string;
  tagIds: string[];
  onOpenChange: (open: boolean) => void;
  focusTransaction: () => void;
}): JSX.Element {
  const user = useContext(UserContext);
  const tags = useContext(TagsContext);
  const { transactionSelected } = useContext(TransactionRowContext);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { onOpenChange } = props;
  useEffect(() => {
    onOpenChange(isOpen);
  }, [isOpen, onOpenChange]);

  const { refs, floatingStyles, context, placement } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(2),
      flip(),
      shift(),
      floatingSize({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });
  const click = useClick(context, { keyboardHandlers: false });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const sortedTags = Array.from(tags.values()).sort(
    (a, b) => a.order_position - b.order_position,
  );
  const tagEntries = sortedTags.map((tag) => ({
    id: tag.id,
    text: tag.name,
    icon: <TagDot colorId={tag.color} size="sm" />,
  }));

  const selectedTagIdSet = new Set(props.tagIds);
  const selectedTags = sortedTags.filter((tag) => selectedTagIdSet.has(tag.id));

  function updateTagIds(newTagIds: string[]) {
    firestoreUpdateTransaction(
      user.uid,
      props.transactionId,
      {
        manual_data: { tag_ids: newTagIds },
      },
      null,
    );
  }

  async function handleCreateTag(name: string) {
    const minOrder = sortedTags.reduce(
      (acc, t) => Math.min(acc, t.order_position),
      0,
    );
    const newTagId = await firestoreCreateTag(user.uid, {
      name,
      color: pickNextTagColorId(sortedTags.length),
      order_position: minOrder - 1,
      created_timestamp_secs: dayjs().unix(),
    });
    updateTagIds([...props.tagIds, newTagId]);
  }

  return (
    <TransactionColumnSizer column={TransactionColumn.TAGS}>
      <div
        ref={refs.setReference}
        {...getReferenceProps({
          onClick: (e) => e.stopPropagation(),
          onMouseDown: (e) => e.stopPropagation(),
          onKeyDown: (e) => {
            if (isOpen) e.stopPropagation();
          },
        })}
        className={buildClasses(
          "relative",
          "w-full",
          "h-full",
          "cursor-pointer",
          "group/tagcell",
          "border",
          "rounded-md",
          "box-border",
          "transition-all",
          {
            if: transactionSelected,
            then: "bg-purple-50",
            else: "bg-transparent",
          },
          {
            if: isOpen,
            then: buildClasses(
              "border-purple",
              "shadow-glow",
              "shadow-purple-500/20",
            ),
            else: buildClasses({
              if: transactionSelected,
              then: "border-purple-200",
              else: buildClasses(
                "border-transparent",
                "hover:border-on-surface-200",
              ),
            }),
          },
        )}
      >
        <div
          className={buildClasses(
            "w-full",
            "h-full",
            "flex",
            "items-center",
            "gap-1",
            "overflow-x-auto",
            "pl-3",
            { if: isOpen, then: "pr-14", else: "pr-8" },
          )}
          style={{
            maskImage: isOpen
              ? "linear-gradient(to right, black calc(100% - 64px), transparent calc(100% - 52px))"
              : "linear-gradient(to right, black calc(100% - 32px), transparent calc(100% - 16px))",
            WebkitMaskImage: isOpen
              ? "linear-gradient(to right, black calc(100% - 64px), transparent calc(100% - 52px))"
              : "linear-gradient(to right, black calc(100% - 32px), transparent calc(100% - 16px))",
          }}
        >
          {selectedTags.length === 0 ? (
            <span
              className={buildClasses(
                "text-on-surface-400",
                "text-sm",
                "truncate",
                {
                  if: transactionSelected,
                  then: "visible",
                  else: buildClasses("invisible", "group-hover/txrow:visible"),
                },
                "flex",
                "items-center",
                "gap-1",
              )}
            >
              <RyeIcon name="sell" size="sm" />
              Add tags
            </span>
          ) : (
            selectedTags.map((tag) => {
              const color = getTagColor(tag.color);
              return (
                <span
                  key={tag.id}
                  title={tag.name}
                  className={buildClasses(
                    "flex-shrink-0",
                    "px-1.5",
                    "py-0.5",
                    "rounded",
                    "text-xs",
                  )}
                  style={{ backgroundColor: color.bg, color: color.text }}
                >
                  {tag.name}
                </span>
              );
            })
          )}
        </div>
        {isOpen && selectedTags.length > 0 && (
          <button
            type="button"
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              updateTagIds([]);
            }}
            aria-label="Clear all tags"
            className={buildClasses(
              "absolute",
              "right-7",
              "top-1/2",
              "-translate-y-1/2",
              "z-10",
              "flex",
              "items-center",
              "justify-center",
              "text-on-surface-500",
              "hover:text-on-surface-800",
            )}
          >
            <RyeIcon name="close" size="sm" />
          </button>
        )}
        <div
          className={buildClasses(
            "absolute",
            "right-2",
            "top-1/2",
            "-translate-y-1/2",
            "flex",
            "items-center",
            "pointer-events-none",
            "text-on-surface-500",
            {
              if: isOpen,
              then: "-scale-y-100",
              else: buildClasses("invisible", "group-hover/tagcell:visible"),
            },
          )}
        >
          <RyeIcon name="keyboard_arrow_down" />
        </div>
        {isOpen && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className={buildClasses("z-[2000000000]")}
              onClick={(e) => e.stopPropagation()}
            >
              <RyeDropDownMultiSelect<string>
                selectedEntryIds={props.tagIds}
                entries={tagEntries}
                onSelectionChange={updateTagIds}
                onCreateNew={handleCreateTag}
                onEsc={() => {
                  setIsOpen(false);
                  props.focusTransaction();
                }}
                onClose={() => {}}
                showSelectedItemsRow={false}
                alwaysExpanded
                flipped={placement.startsWith("top")}
                searchable
                placeholder="Search or create tag"
                size="sm"
                width="full"
                variant="outlined"
              />
            </div>
          </FloatingPortal>
        )}
      </div>
    </TransactionColumnSizer>
  );
}
